import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { ianaTimezoneSchema } from '../timezone';
import { reminderTimeSchema } from '../reminders/schema';

// Wire contracts for the member notification-preferences endpoints.
//
// The response is parsed by installed clients, so it stays non-strict and its
// open vocabularies are plain strings: a category, backing, or icon added
// server-side must reach an installed client as data it can degrade (hide the
// row, render toggle-only, fall back to a default glyph) rather than as a
// parse failure that takes the whole screen down.

export const notificationPreferenceScheduleSchema = z.object({
  time: reminderTimeSchema,
  // The reminder's own pinned zone.
  timezone: ianaTimezoneSchema,
});
export type NotificationPreferenceSchedule = z.infer<typeof notificationPreferenceScheduleSchema>;

export const notificationPreferenceCategorySchema = z.object({
  key: z.string().min(1),
  labelKey: z.string().min(1),
  titleKey: z.string().min(1).optional(),
  descriptionKey: z.string().min(1),
  iconKey: z.string().min(1),
  backing: z.string().min(1),
  order: z.number().int(),
  enabled: z.boolean(),
  // Present only for a reminder-backed category whose reminder row holds one.
  schedule: notificationPreferenceScheduleSchema.optional(),
});
export type NotificationPreferenceCategory = z.infer<typeof notificationPreferenceCategorySchema>;

export const notificationPreferencesDataSchema = z.object({
  categories: z.array(notificationPreferenceCategorySchema),
});
export type NotificationPreferencesData = z.infer<typeof notificationPreferencesDataSchema>;

export const notificationPreferencesResponseSchema = apiEnvelope(notificationPreferencesDataSchema);
export const notificationPreferenceResponseSchema = apiEnvelope(
  notificationPreferenceCategorySchema,
);

export const notificationPreferenceUpdateRequestSchema = z
  .object({
    enabled: z.boolean(),
    // Reminder-backed categories only; a time edit is the same write.
    schedule: notificationPreferenceScheduleSchema.strict().optional(),
  })
  .strict();
export type NotificationPreferenceUpdateRequest = z.infer<
  typeof notificationPreferenceUpdateRequestSchema
>;

export const notificationPreferenceCategoryParamsSchema = z
  .object({ categoryKey: z.string().min(1).max(64) })
  .strict();
